import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Star,
  Security,
  CleaningServices,
  Air,
  Kitchen,
  Bathtub,
  Weekend,
  Layers,
  Verified,
  Chat,
  EmojiEvents,
  ArrowBack,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

// Charter section data
const charterSections = [
  {
    number: '1',
    title: 'Arrival, Respect & Setup',
    icon: Security,
    items: [
      'Uniformed, insured, background-checked cleaners',
      'Arrival within the agreed window + "on the way" text',
      'Quick walkthrough with client (if present) to confirm: Priority areas, Delicate surfaces, "Do not touch" zones, Pets and any special instructions',
      'Shoes covered or house-specific policy followed (shoe covers or indoor shoes)',
      'Equipment staged in a way that does not block doorways or paths',
      'Clean, labeled bottles and tools only – no "mystery" chemicals',
    ],
    clientValue: 'You see a professional crew that treats your home like a private suite, not a job site.',
  },
  {
    number: '2',
    title: 'Hygiene, Safety & Cross-Contamination Control',
    icon: CleaningServices,
    items: [
      'Fresh cloths and mop heads for every home',
      'Color-coded cloths: One color for kitchen surfaces, One for general areas, One dedicated for bathrooms/toilets',
      'Bathroom cloths are never reused in other rooms',
      'Gloves worn for bathrooms and chemical handling',
      'Trash bagged and tied securely; taken to the designated bin area',
      'No use of heavy bleach or harsh chemicals unless requested or necessary',
      'Clear boundaries: we do not handle biohazard waste, infestations, or mold remediation – those get flagged, not hidden',
    ],
    clientValue: 'Higher hygiene, safer home, and no invisible cross-contamination between rooms.',
  },
  {
    number: '3',
    title: 'Whole-Home Dust & Air Reset',
    icon: Air,
    items: [
      'We always work top-to-bottom, left-to-right to avoid re-dirtying cleaned areas',
      'Ceiling corners dusted',
      'High cobweb removal',
      'Light fixtures (reachable) dusted',
      'Air vents dusted, then wiped if needed',
      'Ceiling fans cleaned and blades dusted',
      'Window sills, frames, and ledges dusted',
      'Furniture, décor, picture frames and lamps dusted carefully',
      'Baseboards cleaned room-by-room',
      'HEPA vacuum used on floors, corners, and under accessible furniture',
    ],
    clientValue: 'Dust is the #1 thing cheap cleaners miss. We target it first, throughout the house.',
  },
  {
    number: '4',
    title: 'Kitchen – Luxury Detailing & Sanitizing',
    icon: Kitchen,
    subsections: [
      {
        subtitle: 'Surfaces & Counters',
        items: [
          'Clear small items (where possible), wipe and replace',
          'Counters cleaned and sanitized with material-appropriate product (stone, quartz, laminate, etc.)',
          'Backsplash degreased where needed',
          'Handles, pulls and switches sanitized',
        ],
      },
      {
        subtitle: 'Appliances',
        items: [
          'Stove top degreased; burners and grates wiped',
          'Front of oven, fridge, dishwasher wiped and polished',
          'Stainless steel cleaned along the grain and polished streak-free',
          'Microwave interior cleaned thoroughly (walls, ceiling, plate)',
        ],
      },
      {
        subtitle: 'Sink Area',
        items: [
          'Sink scrubbed, rinsed, dried and shined',
          'Faucet and handles polished (water spots addressed where possible)',
          'Drain area cleaned so there is no visible grime',
        ],
      },
      {
        subtitle: 'Cabinets & Extras',
        items: [
          'Cabinet faces wiped (fingerprints, splashes, smudges)',
          'Glass doors and display areas cleaned (reachable)',
        ],
      },
    ],
    clientValue: 'The heart of your home looks, feels, and smells like a luxury hotel kitchen, not "just wiped."',
  },
  {
    number: '5',
    title: 'Bathrooms – Restoration-Level Clean',
    icon: Bathtub,
    subsections: [
      {
        subtitle: 'General',
        items: [
          'Vent and light dusting',
          'Mirror cleaned and polished with no streaks',
          'Countertops cleared as feasible, wiped and disinfected',
          'Faucets and fixtures polished until they shine',
        ],
      },
      {
        subtitle: 'Showers & Tubs',
        items: [
          'Walls and floors cleaned with appropriate product',
          'Soap scum and product residue reduced or removed as far as chemistry allows',
          'Glass doors washed and dried (no heavy streaking left)',
          'Fixtures, drains and handles detailed',
        ],
      },
      {
        subtitle: 'Toilets',
        items: [
          'Inside bowl cleaned and disinfected',
          'Exterior wiped, including base and back of toilet',
          'Flush handle disinfected',
          'Area around the toilet (floor, wall spot cleaning) also cleaned',
        ],
      },
      {
        subtitle: 'Extras',
        items: [
          'Towel bars wiped',
          'Trash emptied and bag replaced',
          'Towels folded or hung hotel-style if provided by client',
        ],
      },
    ],
    clientValue: 'Bathrooms don\'t just look cleaner – they feel sanitized and "reset".',
  },
  {
    number: '6',
    title: 'Living Areas & Bedrooms – Visual Luxury Finish',
    icon: Weekend,
    items: [
      'All horizontal surfaces dusted and wiped (tables, nightstands, desks)',
      'Lamps, side tables, and visible décor dusted and positioned neatly',
      'Smudge spots on doors, frames and light switches treated',
      'Mirrors and glass surfaces cleaned streak-free',
      'Cushions fluffed, throws folded',
      'Beds made neatly; if fresh linens are provided and requested, sheets can be changed',
      'Floors vacuumed (including under beds and sofas where accessible)',
      'Hard floors mopped with correct product and tool',
      'Carpets and rugs vacuumed with HEPA vacuum',
    ],
    clientValue: 'The home looks "photographed," not just cleaned – edges, lines, and surfaces are visually organized.',
  },
  {
    number: '7',
    title: 'Floor Care – Material-Aware Excellence',
    icon: Layers,
    items: [
      'Dry debris removed first with HEPA vacuum',
      'Edge and corner tools used along baseboards and furniture feet',
      'Mop water / solution changed frequently – no gray water dragged around',
      'Matching product to material: Neutral cleaner for hardwood/laminate, Proper products for tile, vinyl, or stone',
      'Path of travel planned to avoid footprints and streaks',
      'Quick visual double-check for missed spots, lines, or residue',
    ],
    clientValue: 'Floors feel clean under bare feet and look flawless in natural light.',
  },
  {
    number: '8',
    title: 'Final Detailing, Check & Presentation',
    icon: Verified,
    items: [
      'Door handles and high-touch switches wiped in main areas',
      'All visible trash taken out to designated location',
      'Supplies restored neatly (paper towels, soap, etc. if provided)',
      'Rooms visually checked from the doorway – we correct anything off: crooked cushions, streak on glass, missed dust on a shelf',
    ],
    signatureTouches: [
      'Toilet paper folded (hotel style)',
      'Bed pillows aligned',
      'Towels folded / hung neatly',
    ],
    optionalItems: [
      'Before/after photo set',
      'Quick summary note of anything noticed (maintenance issues, stains, loose fixtures, etc.)',
    ],
    clientValue: 'You walk into a finished, composed space – not a place someone just "ran a vacuum through."',
  },
  {
    number: '9',
    title: 'Communication, Notes & Special Requests',
    icon: Chat,
    items: [
      'Client preferences logged (fragrance-free, pet sensitivities, product choices, alarm or gate code, parking specifics)',
      'Same preferences reused on repeat visits – no need to re-explain',
      'If something cannot be done (e.g., unsafe height, special machine needed), it is communicated honestly, not partially attempted and left messy',
      'Any accidental damage is reported immediately, with solutions proposed – no hiding, ever',
    ],
    clientValue: 'You feel informed, respected, and never left guessing.',
  },
];

const LuxuryCleaningCharter = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <TopBar />
      <Seo
        title="The Luxury Cleaning Charter"
        description="Our commitment to premium service: the standards, checklists, and guarantees behind every 5 Fairies cleaning."
        path="/luxury-cleaning-charter"
      />

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, sm: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 100%)',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'gold.main',
              letterSpacing: 4,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              mb: 2,
              display: 'block',
            }}
          >
            5 Fairies Cleaning Services
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Luxury Cleaning Process{' '}
            <Box component="span" sx={{ color: 'gold.main' }}>Charter</Box>
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontStyle: 'italic',
              fontWeight: 400,
              color: 'secondary.main',
              opacity: 0.9,
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
            }}
          >
            Our concrete, step-by-step standard for hotel-grade results
          </Typography>

          <Divider
            sx={{
              bgcolor: 'gold.main',
              height: 3,
              width: 80,
              mx: 'auto',
              mb: 4,
            }}
          />

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
              lineHeight: 1.8,
              maxWidth: 700,
              mx: 'auto',
              color: 'secondary.light',
            }}
          >
            Every cleaning we perform follows this <strong>written process</strong>.
            It's how we guarantee the same high standard{' '}
            <Box component="span" sx={{ color: 'gold.main', fontWeight: 600 }}>
              regardless of which team comes to your home
            </Box>.
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Back Button */}
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBack />}
          sx={{
            mb: 4,
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.04)',
            },
          }}
        >
          Back to Home
        </Button>

        {/* Charter Sections */}
        {charterSections.map((section, index) => (
          <Paper
            key={section.number}
            elevation={0}
            sx={{
              mb: { xs: 3, md: 4 },
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'gold.main',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              },
            }}
          >
            {/* Section Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 2, sm: 3 },
                mb: 3,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'gold.main',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <section.icon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'gold.dark',
                    letterSpacing: 2,
                    fontSize: '0.75rem',
                  }}
                >
                  Section {section.number}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    lineHeight: 1.3,
                  }}
                >
                  {section.title}
                </Typography>
              </Box>
            </Box>

            {/* Items or Subsections */}
            {section.items && (
              <List sx={{ pl: { xs: 0, sm: 2 } }}>
                {section.items.map((item, itemIndex) => (
                  <ListItem
                    key={itemIndex}
                    sx={{
                      px: 0,
                      py: 0.75,
                      alignItems: 'flex-start',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      <CheckCircle
                        sx={{
                          color: 'gold.main',
                          fontSize: 20,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: 'text.primary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* Subsections (for Kitchen and Bathroom) */}
            {section.subsections && (
              <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                {section.subsections.map((subsection, subIndex) => (
                  <Box key={subIndex} sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        mb: 1.5,
                        pl: 1,
                        borderLeft: '3px solid',
                        borderColor: 'gold.main',
                      }}
                    >
                      {subsection.subtitle}
                    </Typography>
                    <List>
                      {subsection.items.map((item, itemIndex) => (
                        <ListItem
                          key={itemIndex}
                          sx={{
                            px: 0,
                            py: 0.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckCircle
                              sx={{
                                color: 'gold.main',
                                fontSize: 18,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              fontSize: { xs: '0.9rem', sm: '0.95rem' },
                              lineHeight: 1.6,
                              color: 'text.primary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            )}

            {/* Signature Touches (Section 8) */}
            {section.signatureTouches && (
              <Box sx={{ mb: 3, pl: { xs: 0, sm: 2 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Star sx={{ color: 'gold.main', fontSize: 20 }} />
                  Signature Touch
                </Typography>
                <List>
                  {section.signatureTouches.map((item, itemIndex) => (
                    <ListItem
                      key={itemIndex}
                      sx={{ px: 0, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: 'gold.main', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          fontSize: { xs: '0.9rem', sm: '0.95rem' },
                          lineHeight: 1.6,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Optional Items (Section 8) */}
            {section.optionalItems && (
              <Box sx={{ mb: 3, pl: { xs: 0, sm: 2 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    mb: 1,
                  }}
                >
                  Optional (on request):
                </Typography>
                <List>
                  {section.optionalItems.map((item, itemIndex) => (
                    <ListItem
                      key={itemIndex}
                      sx={{ px: 0, py: 0.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: 'grey.400', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          lineHeight: 1.5,
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Client Value Box */}
            <Box
              sx={{
                mt: 3,
                p: { xs: 2, sm: 3 },
                bgcolor: 'grey.50',
                borderRadius: 2,
                borderLeft: '4px solid',
                borderColor: 'gold.main',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'gold.dark',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: 1,
                  mb: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Client Value
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.primary',
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  lineHeight: 1.6,
                }}
              >
                {section.clientValue}
              </Typography>
            </Box>
          </Paper>
        ))}

        {/* Guarantee Section */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, md: 6 },
            mb: { xs: 4, md: 6 },
            p: { xs: 4, sm: 5, md: 6 },
            borderRadius: 3,
            bgcolor: 'primary.main',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <EmojiEvents
            sx={{
              fontSize: { xs: 48, sm: 64 },
              color: 'gold.main',
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            5 Fairies{' '}
            <Box component="span" sx={{ color: 'gold.main' }}>
              Luxury Guarantee
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 400,
              color: 'secondary.light',
            }}
          >
            If any area we cleaned does not meet our Luxury Cleaning Process Charter,{' '}
            <Box component="span" sx={{ color: 'gold.main', fontWeight: 600 }}>
              we return and correct it at no cost
            </Box>.
          </Typography>
          <Divider
            sx={{
              bgcolor: 'gold.main',
              height: 2,
              width: 60,
              mx: 'auto',
              my: 4,
              opacity: 0.5,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', sm: '1rem' },
              opacity: 0.85,
              lineHeight: 1.7,
            }}
          >
            This charter is not just marketing –<br />
            it is the <strong>checklist we actually use</strong> to train, supervise, and evaluate our teams.
          </Typography>
        </Paper>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', py: { xs: 2, md: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            Ready to experience the difference?
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: { xs: 4, sm: 6 },
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Get Your Custom Cleaning Estimate
          </Button>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default LuxuryCleaningCharter;
